import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import GuestBanner from '../components/GuestBanner';
import {
  GraduationCap,
  Globe,
  Activity,
  Dog,
  Heart,
  Microscope,
  FlaskConical,
  Search,
  ExternalLink,
  Users,
  Clock,
  MapPin,
  Dna,
  Brain,
  Eye,
  Bone
} from 'lucide-react';

const ResearchOpportunities = () => {
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const researchGroups = [
    {
      id: 1,
      name: 'Cornell Canine Genetics Lab',
      organization: 'Baker Institute',
      description: 'Identifies genetic variants for hereditary diseases in dogs',
      url: 'https://caninegeneticslab.vet.cornell.edu',
      category: 'genetics',
      icon: GraduationCap,
      color: 'red',
      tags: ['Hereditary Diseases', 'Genetic Testing'],
      location: 'Ithaca, NY'
    },
    {
      id: 2,
      name: 'Purdue Canine Genetics Lab',
      organization: "Dr. Ekenstedt's Lab",
      description: 'Solves genetic diseases through cutting-edge DNA testing and research',
      url: 'https://vet.purdue.edu/discovery/ekenstedt/',
      category: 'genetics',
      icon: GraduationCap,
      color: 'amber',
      tags: ['Genetic Diseases', 'DNA Testing'],
      location: 'West Lafayette, IN'
    },
    {
      id: 3,
      name: 'Canine Genetics Centre (UK)',
      organization: "Dr. Mellersh's Team",
      description: 'Specializes in eye diseases and neurological disorders across 50+ breeds',
      url: 'https://www.canine-genetics.org.uk',
      category: 'genetics',
      icon: Globe,
      color: 'blue',
      tags: ['Eye Diseases', 'Neurological', '30+ Variants'],
      location: 'United Kingdom'
    },
    {
      id: 4,
      name: 'Dog Aging Project',
      organization: 'University of Washington',
      description: 'Analyzes 40-60 million genetic variations per dog for aging and longevity studies',
      url: 'https://dogagingproject.org',
      category: 'citizen-science',
      icon: Activity,
      color: 'green',
      tags: ['Citizen Science', 'Aging', 'Longevity'],
      location: 'Seattle, WA',
      participationOpen: true
    },
    {
      id: 5,
      name: "Darwin's Dogs",
      organization: 'Broad Institute / UMass',
      description: 'Research into behavioral traits in dogs - FREE DNA testing for participants',
      url: 'https://darwinsark.org/darwins-dogs',
      category: 'behavior',
      icon: Dog,
      color: 'purple',
      tags: ['Behavior', 'Free DNA Testing', 'Citizen Science'],
      location: 'Cambridge, MA',
      participationOpen: true,
      highlight: true
    },
    {
      id: 6,
      name: 'Canine Cancer Genome Project',
      organization: 'TGen',
      description: 'Multi-omics profiling for osteosarcoma, lymphoma, and soft tissue sarcoma',
      url: 'https://www.tgen.org/news/canine-cancer-genome-project-enters-phase-ii-omics/',
      category: 'cancer',
      icon: Heart,
      color: 'pink',
      tags: ['Cancer Research', 'Osteosarcoma', 'Lymphoma'],
      location: 'Phoenix, AZ'
    },
    {
      id: 7,
      name: 'NHGRI Dog Genome Project',
      organization: 'NIH/NHGRI',
      description: 'Federal research into canine genetics and hereditary diseases',
      url: 'https://research.nhgri.nih.gov/dog_genome/index.shtml',
      category: 'genetics',
      icon: Microscope,
      color: 'indigo',
      tags: ['Government', 'Hereditary Diseases', 'Breed Research'],
      location: 'Bethesda, MD'
    },
    {
      id: 8,
      name: 'UC Davis Veterinary Genetics Lab',
      organization: 'UC Davis',
      description: 'Comprehensive genetic testing and research for dogs and other animals',
      url: 'https://vgl.ucdavis.edu/canine',
      category: 'genetics',
      icon: FlaskConical,
      color: 'yellow',
      tags: ['Genetic Testing', 'Breed Research'],
      location: 'Davis, CA'
    },
    {
      id: 9,
      name: 'Morris Animal Foundation',
      organization: 'Golden Retriever Lifetime Study',
      description: 'Studying cancer and other diseases in Golden Retrievers with 3,000+ enrolled dogs',
      url: 'https://www.morrisanimalfoundation.org/golden-retriever-lifetime-study',
      category: 'cancer',
      icon: Heart,
      color: 'amber',
      tags: ['Cancer Research', 'Golden Retrievers', 'Longitudinal Study'],
      location: 'Denver, CO',
      participationOpen: false
    },
    {
      id: 10,
      name: 'AKC Canine Health Foundation',
      organization: 'American Kennel Club',
      description: 'Funds research grants for canine health studies across all breeds',
      url: 'https://www.akcchf.org',
      category: 'foundation',
      icon: Users,
      color: 'blue',
      tags: ['Funding', 'All Breeds', 'Health Research'],
      location: 'New York, NY'
    }
  ];

  const categories = [
    { id: 'all', label: 'All Research', icon: Microscope },
    { id: 'genetics', label: 'Genetics', icon: Dna },
    { id: 'cancer', label: 'Cancer', icon: Heart },
    { id: 'behavior', label: 'Behavior', icon: Brain },
    { id: 'citizen-science', label: 'Citizen Science', icon: Users },
    { id: 'foundation', label: 'Foundations', icon: GraduationCap }
  ];

  const colorClasses = {
    red: 'bg-red-100 text-red-600',
    amber: 'bg-amber-100 text-amber-600',
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    purple: 'bg-purple-100 text-purple-600',
    pink: 'bg-pink-100 text-pink-600',
    indigo: 'bg-indigo-100 text-indigo-600',
    yellow: 'bg-yellow-100 text-yellow-600'
  };

  const filteredResearch = researchGroups.filter(item => {
    const matchesSearch = searchQuery === '' || 
      item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  const openParticipation = researchGroups.filter(r => r.participationOpen);

  return (
    <div className="min-h-screen bg-gray-50">
      {!user && <GuestBanner />}
      
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Microscope className="w-8 h-8 text-primary" />
            Research Opportunities
          </h1>
          <p className="text-muted-foreground mt-2">
            Discover ways to contribute to canine health research and help advance veterinary science
          </p>
        </div>

        {/* Participation Callout */}
        {openParticipation.length > 0 && (
          <Card className="mb-6 border-green-200 bg-green-50">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                  <Users className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-green-800">Open for Participation!</h3>
                  <p className="text-sm text-green-700">
                    {openParticipation.length} research {openParticipation.length === 1 ? 'project is' : 'projects are'} currently 
                    accepting new participants. Your dog could help advance canine health research!
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Search and Filter */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
            <Input
              placeholder="Search research projects..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="research-search"
            />
          </div>
        </div>

        {/* Category Tabs */}
        <Tabs value={selectedCategory} onValueChange={setSelectedCategory} className="mb-6">
          <TabsList className="flex-wrap h-auto gap-1">
            {categories.map(cat => (
              <TabsTrigger key={cat.id} value={cat.id} className="flex items-center gap-1.5">
                <cat.icon className="w-4 h-4" />
                <span className="hidden sm:inline">{cat.label}</span>
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        {/* Results Count */}
        <p className="text-sm text-muted-foreground mb-4">
          Showing {filteredResearch.length} of {researchGroups.length} research opportunities
        </p>

        {/* Research Grid */}
        <div className="grid md:grid-cols-2 gap-4">
          {filteredResearch.map(research => {
            const IconComponent = research.icon;
            return (
              <a 
                key={research.id}
                href={research.url} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="block group"
                data-testid={`research-card-${research.id}`}
              >
                <Card className={`h-full transition-all hover:shadow-md hover:border-primary ${research.highlight ? 'ring-2 ring-green-200' : ''}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-lg ${colorClasses[research.color]} flex items-center justify-center flex-shrink-0`}>
                        <IconComponent className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <h4 className="font-medium group-hover:text-primary transition-colors">
                              {research.name}
                            </h4>
                            <p className="text-xs text-muted-foreground">{research.organization}</p>
                          </div>
                          {research.participationOpen && (
                            <Badge className="bg-green-100 text-green-700 text-xs flex-shrink-0">
                              Open
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                          {research.description}
                        </p>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {research.tags.slice(0, 3).map(tag => (
                            <Badge key={tag} variant="secondary" className="text-xs">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                        <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {research.location}
                          </span>
                          <span className="flex items-center gap-1 text-primary">
                            <ExternalLink className="w-3 h-3" />
                            Visit Site
                          </span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </a>
            );
          })}
        </div>

        {filteredResearch.length === 0 && (
          <Card className="p-8 text-center">
            <Microscope className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-medium text-lg">No matching research found</h3>
            <p className="text-muted-foreground">Try adjusting your search or filters</p>
          </Card>
        )}

        {/* How to Participate Section */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              How to Participate in Research
            </CardTitle>
            <CardDescription>
              Contributing to canine research helps all dogs live healthier lives
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="p-4 bg-blue-50 rounded-lg">
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mb-2">
                  <span className="text-blue-600 font-bold">1</span>
                </div>
                <h4 className="font-medium mb-1">Find a Study</h4>
                <p className="text-sm text-muted-foreground">
                  Browse the research projects above and find one that interests you or matches your dog's breed/condition.
                </p>
              </div>
              <div className="p-4 bg-green-50 rounded-lg">
                <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center mb-2">
                  <span className="text-green-600 font-bold">2</span>
                </div>
                <h4 className="font-medium mb-1">Check Eligibility</h4>
                <p className="text-sm text-muted-foreground">
                  Visit the project's website to review requirements. Some studies need specific breeds or conditions.
                </p>
              </div>
              <div className="p-4 bg-purple-50 rounded-lg">
                <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center mb-2">
                  <span className="text-purple-600 font-bold">3</span>
                </div>
                <h4 className="font-medium mb-1">Enroll</h4>
                <p className="text-sm text-muted-foreground">
                  Follow the project's enrollment process. This may include surveys, DNA samples, or regular check-ins.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ResearchOpportunities;
